import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { X, MessageCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const ChatNotificationPopup = () => {
  const navigate = useNavigate();
  const { notifications, dismissNotification, clearAllNotifications } = useNotifications();

  // Only show unread notifications
  const unreadNotifications = notifications.filter(n => !n.read);

  if (unreadNotifications.length === 0) return null;

  const handleOpenChat = (conversationId) => {
    navigate(`/conversations/${conversationId}`);
    dismissNotification(unreadNotifications.find(n => n.conversationId === conversationId)?.id);
  };

  return (
    <div 
      className="fixed bottom-20 right-4 z-50 w-80 max-h-96 overflow-y-auto"
      data-testid="chat-notification-popup"
    >
      <div className="bg-card border rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary/10 border-b">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">New Messages</span>
            <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
              {unreadNotifications.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={clearAllNotifications}
            data-testid="clear-all-notifications-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Notifications List */}
        <div className="divide-y">
          {unreadNotifications.map((notification) => (
            <div
              key={notification.id}
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-1">
                    Re: {notification.eventTitle}
                  </p>
                  <p className="text-sm font-medium truncate">
                    {notification.senderName}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {notification.message.replace(`${notification.senderName}: `, '')}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => dismissNotification(notification.id)}
                    data-testid={`dismiss-notification-${notification.id}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 text-xs h-7"
                onClick={() => handleOpenChat(notification.conversationId)}
                data-testid={`open-chat-${notification.conversationId}`}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open Chat
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChatNotificationPopup;
